import {useDateQuery} from '@ls/date-module';
import PropTypes from 'prop-types';
import React from 'react';
import {useUser} from '@ls/user-module';
import DP, {registerLocale } from 'react-datepicker';
import styled, {createGlobalStyle} from 'styled-components';
import CustomInput from './CustomInput';
import QuickSelect from './QuickSelect';
import SymbolicInput from './SymbolicInput';
import {format, parse} from 'date-fns';
import enUs from 'date-fns/locale/en-US';
import enGb from 'date-fns/locale/en-GB';
import { LabelPosition } from '../Const';

registerLocale('en-US', enUs);
registerLocale('en-GB', enGb);

const DpOverrideStyle = createGlobalStyle`
  .react-datepicker {
    display: flex;
    flex-direction: column;
  }
  .react-datepicker-popper {
    z-index: 10;
  }
  .react-datepicker-wrapper {
    width: 100%;
  }
`;
const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 5px;
  align-items: center;
  width: 100%;
  border-radius: 3px;
  ${({variation}) => {
    switch (variation) {
      default:
      case 0:
        break;
      case 1:
        return `
              border: none;
              background: #dbdbdb;
              padding: 10px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
            `;
    }
  }}
`;

/*Externally selected date is always stored as yyyy-MM-dd. This converts it to Date object for react-datepicker*/
const parseDateString = (s) => {
  if (s) {
    return parse(s, 'yyyy-MM-dd', new Date());
  } else {
    return null;
  }
}

const CalendarDatePicker = (props) => {
  const {
    onChange, variation, disabled, placeholderText, placeholder, enableQuickSelect, quickSelectButtons,
    symbolicDate, onChangeSymbolicDate, size, clearable, label, labelPosition
  } = props;
  const dpRef = React.useRef(null);
  const user = useUser();
  const locale = user?.settings?.locale ?? 'en-US';

  /* Ensure that symbolic date is fetched and set as the date */
  const symbolicDateQuery = useDateQuery(symbolicDate);
  React.useEffect(() => {
    if (symbolicDateQuery.data && symbolicDateQuery.data !== props.selected) {
      onChange(symbolicDateQuery.data);
    }
  }, [onChange, props.selected, symbolicDateQuery.data]);

  /* convert YYYY-MM-DD dates to JS date objects */
  const selected = React.useMemo(() => {
    return parseDateString(props.selected);
  }, [props.selected]);
  const startDate = React.useMemo(() => {
    return parseDateString(props.startDate);
  }, [props.startDate]);
  const endDate = React.useMemo(() => {
    return parseDateString(props.endDate);
  }, [props.endDate]);
  const minDate = React.useMemo(() => {
    return parseDateString(props.minDate);
  }, [props.minDate]);
  const maxDate = React.useMemo(() => {
    return parseDateString(props.maxDate);
  }, [props.maxDate]);

  /* Externally selected date is always stored as yyyy-MM-dd. Formatting it before calling the onchange prop.*/
  const onChangeDatePickerDate = React.useCallback((dateInput) => {
    if (onChangeSymbolicDate) { //selecting literal date means symbolic date should reset
      onChangeSymbolicDate(null);
    }
    if (!dateInput) {
      onChange(null);
    } else {
      onChange(format(dateInput, 'yyyy-MM-dd'));
    }
  }, [onChange, onChangeSymbolicDate]);

  const onSelectQuickSelectDate = React.useCallback((date) => {
    onChange(date);
    if (onChangeSymbolicDate) { //selecting literal date means symbolic date should reset
      onChangeSymbolicDate(null);
    }
  }, [onChange, onChangeSymbolicDate]);

  const clearSelectedDate = React.useCallback(() => onChangeDatePickerDate(null), [onChangeDatePickerDate]);

  const loading = symbolicDateQuery.isFetching || props.loading;

  return (
    <Root variation={variation} size={size}>
      <DpOverrideStyle/>
      <DP
        ref={dpRef}
        customInput={
          <CustomInput
            clearSelectedDate={clearSelectedDate}
            disabled={disabled}
            dpRef={dpRef}
            size={size}
            clearable={clearable}
            symbolicDate={symbolicDate}
            loading={loading}
            label={label}
            labelPosition={labelPosition}
          />
        }
        dropdownMode="select"
        locale={locale}
        dateFormat="P"
        {...props}
        selected={selected}
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        maxDate={maxDate}
        onChange={onChangeDatePickerDate}
        placeholderText={placeholderText ?? placeholder}
      >
        {enableQuickSelect && (
          <QuickSelect
            quickSelectButtons={quickSelectButtons}
            onSelectDate={onSelectQuickSelectDate}
            dpRef={dpRef}
            onChangeSymbolicDate={onChangeSymbolicDate}
          />
        )}
        {onChangeSymbolicDate && (
          <SymbolicInput
            disabled={disabled}
            symbolicDate={symbolicDate}
            onChangeSymbolicDate={onChangeSymbolicDate}
            loading={loading}
          />
        )}
        {props.children}
      </DP>
    </Root>
  );
};

CalendarDatePicker.propTypes = {
  ...DP.propTypes,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  variation: PropTypes.number,
  size: PropTypes.string,
  enableQuickSelect: PropTypes.bool,
  quickSelectButtons: PropTypes.array,
  symbolicDate: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.node,
  labelPosition: PropTypes.string,
  clearable: PropTypes.bool
};

CalendarDatePicker.defaultProps = {
  variation: 0,
  size: 'M',
  enableQuickSelect: false,
  quickSelectButtons: ['PYE', 'PQE', 'PME', 'PBD', 'CBD'],
  symbolicDate: undefined,
  placeholder: undefined,
  label: undefined,
  labelPosition: LabelPosition.LEFT,
  clearable: false,
};

export default CalendarDatePicker;

